// hooks/admins/useAdmins.ts
import { useEffect, useState, useCallback } from "react";
import { adminService } from "../../services/adminService";
import { alertSuccess, alertError } from "../../helpers/alertError";
import { Shop } from "@cabukaka/types";
import { useToken } from "@cabukaka/hooks";

export default function useAdmins() {
  const { token } = useToken();
  const [managedShop, setManagedShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ✅ Memoïser fetchManagedShop avec useCallback
  const fetchManagedShop = useCallback(async () => {
    if (!token) {
      setError("Token manquant");
      return null;
    }
    
    setLoading(true);
    try {
      const shop = await adminService.getManagedShop(token);
      setManagedShop(shop);
      return shop;
    } catch (err: any) {
      setError(err.message || "Erreur lors de la récupération de la boutique");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]); // Dépend de token uniquement

  // Mettre à jour la boutique gérée
  const updateManagedShop = useCallback(async (shopId: string) => {
    if (!token) {
      alertError("Une erreur s'est produite");
      return;
    }
    
    setLoading(true);
    try {
      const result = await adminService.manageShop(shopId, token);
      await fetchManagedShop(); // Recharger après mise à jour
      alertSuccess("Boutique associée avec succès");
      return result;
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'association");
      alertError(err.message || "Erreur lors de l'association");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, fetchManagedShop]);

  // Associer un admin à une boutique
  const manageShop = useCallback(async (shopId: string) => {
    if (!token) {
      alert("Token manquant");
      return;
    }
    
    setError(null);
    try {
      const result = await adminService.manageShop(shopId, token);
      await fetchManagedShop();
      alertSuccess("Admin associé à la boutique avec succès");
      return result;
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'association");
      alertError(err.message || "Erreur lors de l'association");
      throw err;
    }
  }, [token, fetchManagedShop]);

  // ✅ useEffect avec dépendances stables
  useEffect(() => {
    if (token) {
      fetchManagedShop();
    }
  }, [token, fetchManagedShop]);

  return {
    managedShop,
    setManagedShop,
    loading,
    error,
    manageShop,
    fetchManagedShop,
    updateManagedShop
  };
}